/**
 * DefaultWeightsSection: per-CLIENT-org fallback shipping weight (kg) by
 * product type code (`org_default_weights`, `defaultWeights` router).
 *
 * A weight on the Shopify product ALWAYS wins; these defaults only fill a line
 * Shopify left weightless, so a carrier that requires a per-item weight
 * (YunExpress) never rejects an order over catalog data the client never
 * filled in. A dual-print code (`2HT`) falls back to its plain code (`HT`)
 * unless it has its own row; `2PACK` is never rewritten. Resolution lives in
 * `resolveDefaultWeightKg` (`@threadheads/shared`), which this surface reuses
 * so "is this code already covered?" can never drift from the backend.
 *
 * Mirrors `PrintSizeRulesSection` / `DesignsSourceSection`:
 *   - Client selector from `bridgeControl.listClients` (deduped by client org)
 *     so a 3PL OWNER (Thread Lab) edits each client's defaults on the client's
 *     behalf. Read + write re-target via the `organizationId` input.
 *   - The section WAITS for that list (skeleton, or an amber row with Retry):
 *     an owner's orders belong to its clients' stores, so with clients the
 *     default target is the FIRST client, and mounting the form for this
 *     organization first would show (and invite edits to) the wrong store's
 *     weights. The resolved default is frozen in state, so a background
 *     refetch can never move an open draft to another org.
 *   - "Applies to" is always visible (a picker with clients, plain text
 *     without), with the rule it encodes: orders use the weights of the store
 *     they belong to.
 *   - Add-row pick-list is the shared `PrintSizeCodeCombobox`, sourced from the
 *     SELECTED org's own `productSync.getCodeTablesForOrg` (`CODE · store
 *     name`), degrading to plain manual entry while loading / empty / errored.
 *     See `architectural-decisions.md` § "Shared 3PL↔client data is surfaced,
 *     never re-typed". Adding a row moves focus to its kg input.
 *   - The form remounts per selected org (`key`), so a draft can never be
 *     written to a different org than the one it was typed for. The selector
 *     locks while there are unsaved edits rather than silently dropping them.
 *
 * Save model: the Console's unified one, and ONLY that. Pending edits register
 * with `useDirtyRegistration` (sticky SettingsSaveBar + area-switch guard);
 * there is no inline Save or Discard. While a row is invalid an inline notice
 * says why, and the bar's Save refuses with the same sentence. Admin only;
 * read-only for everyone else.
 *
 * kg inputs are RAW TEXT (`type="text" inputMode="decimal"`), never
 * `type="number"`: a number input reports '' for a half-typed `0.2e`, which
 * would read as "cleared" (DESIGN.md §12.1).
 */
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AlertTriangle, Info, Plus, RefreshCw, Scale, Trash2 } from 'lucide-react';
import {
  DEFAULT_WEIGHT_MAX_KG,
  DEFAULT_WEIGHT_MIN_KG,
  DEFAULT_WEIGHTS_MAX_CODES,
  RIP_CODE_REGEX,
  resolveDefaultWeightKg,
} from '@threadheads/shared';
import { kgFieldError, parseKg } from '@/lib/parse-kg';
import { trpc } from '@/lib/trpc';
import { cn } from '@/lib/utils';
import { useRole } from '@/hooks/use-role';
import { useOrganization } from '@/contexts/OrganizationContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PrintSizeCodeCombobox } from './PrintSizeCodeCombobox';
import { buildProductTypeCodeOptions, codeTablesStatus, joinStoreNames } from './org-code-labels';
import { useDirtyRegistration } from './console/SettingsDirtyContext';

// ---------------------------------------------------------------------------
// Pure helpers (module-level so identity is stable). The kg parser itself is
// shared with the 3PL shipping weight modal (`@/lib/parse-kg`).
// ---------------------------------------------------------------------------

const toDraft = (weights: Record<string, number>): Record<string, string> =>
  Object.fromEntries(Object.entries(weights).map(([code, kg]) => [code, String(kg)]));

/** True when the draft would save exactly what the server already holds. */
function matchesBaseline(draft: Record<string, string>, baseline: Record<string, number>): boolean {
  const codes = Object.keys(draft);
  if (codes.length !== Object.keys(baseline).length) return false;
  return codes.every((code) => {
    if (!Object.prototype.hasOwnProperty.call(baseline, code)) return false;
    const parsed = parseKg(draft[code] ?? '');
    return parsed.kind === 'ok' && parsed.kg === baseline[code];
  });
}

/**
 * A dual-print code (`2HT`) needs no row of its own when its plain code (`HT`)
 * has one. Asked of the shared resolver with a placeholder weight, so the
 * `2PACK` exclusion and the exact-match-first rule stay the backend's.
 */
const coveredByPlainCode = (code: string, present: ReadonlySet<string>): boolean => {
  const plain = code.slice(1);
  return present.has(plain) && resolveDefaultWeightKg({ [plain]: 1 }, code) !== null;
};

// ---------------------------------------------------------------------------
// Section: help text + client selector; the form remounts per selected org.
// ---------------------------------------------------------------------------

export function DefaultWeightsSection(): JSX.Element {
  const { t } = useTranslation('settings');
  const { isAdmin } = useRole();
  const admin = isAdmin();
  const { organization } = useOrganization();
  const ownOrgId = organization?.id;

  // Active junction clients across the caller's owned locations. Deduped: a
  // client with two location junctions otherwise appears twice.
  const clientsQuery = trpc.bridgeControl.listClients.useQuery();
  const clientOptions = useMemo(() => {
    const byId = new Map<string, string>();
    for (const c of clientsQuery.data?.clients ?? []) {
      if (byId.has(c.clientOrganizationId)) continue;
      byId.set(c.clientOrganizationId, c.clientAlias ?? c.clientName ?? c.clientOrganizationId);
    }
    return [...byId.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  }, [clientsQuery.data]);

  // Data present = ready, even if a later background refetch failed: the
  // error branch below must never unmount a form that is being edited.
  const clientsReady = clientsQuery.data !== undefined;

  // Resolved ONCE, then frozen in state: with clients the default is the
  // first client (an owner's orders belong to its clients' stores). Deriving
  // it on every render would let a refetch that reorders the list move an
  // open draft to another org.
  const [selectedOrgId, setSelectedOrgId] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (selectedOrgId || !ownOrgId || !clientsReady) return;
    setSelectedOrgId(clientOptions[0]?.id ?? ownOrgId);
  }, [selectedOrgId, ownOrgId, clientsReady, clientOptions]);

  // The router defaults to ctx.organizationId when omitted, so "this
  // organization" omits it and both calls target the caller's own row.
  const targetOrgId = !selectedOrgId || selectedOrgId === ownOrgId ? undefined : selectedOrgId;
  const clientName = targetOrgId ? clientOptions.find((c) => c.id === targetOrgId)?.name : undefined;

  // Switching org remounts the form, which would drop unsaved edits; lock the
  // selector instead and say why.
  const [formDirty, setFormDirty] = useState(false);

  const help = (
    <p className="flex max-w-prose items-start gap-2 text-sm text-muted-foreground">
      <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      <span className="leading-relaxed">{t('production.defaultWeights.help')}</span>
    </p>
  );

  if (!clientsReady && clientsQuery.isError) {
    return (
      <div className="space-y-4">
        {help}
        <div className="flex max-w-2xl flex-wrap items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
          <AlertTriangle className="h-5 w-5 shrink-0 text-amber-600" aria-hidden="true" />
          <p className="min-w-0 flex-1 text-sm text-amber-800">{t('production.defaultWeights.clientsError')}</p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-10 gap-2 border-amber-300 bg-white/70 text-amber-900 hover:bg-white"
            onClick={() => void clientsQuery.refetch()}
          >
            <RefreshCw className="h-4 w-4" aria-hidden="true" />
            {t('production.common.retry')}
          </Button>
        </div>
      </div>
    );
  }

  if (!selectedOrgId) {
    return (
      <div className="space-y-4">
        {help}
        <div className="space-y-4" role="status" aria-busy="true">
          <span className="sr-only">{t('production.defaultWeights.clientsLoading')}</span>
          <div className="grid max-w-xs gap-1.5">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-11 w-full" />
          </div>
          <div className="max-w-2xl space-y-2">
            <Skeleton className="h-11 w-full" />
            <Skeleton className="h-11 w-full" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {help}

      <div className="grid max-w-xs gap-1.5">
        {clientOptions.length > 0 ? (
          <>
            <Label htmlFor="default-weights-client">{t('production.defaultWeights.appliesTo')}</Label>
            <Select value={selectedOrgId} onValueChange={setSelectedOrgId} disabled={!ownOrgId || formDirty}>
              <SelectTrigger
                id="default-weights-client"
                className="h-11"
                aria-describedby={
                  formDirty
                    ? 'default-weights-client-help default-weights-client-locked'
                    : 'default-weights-client-help'
                }
              >
                <SelectValue placeholder={t('production.defaultWeights.selectOrganization')} />
              </SelectTrigger>
              <SelectContent>
                {ownOrgId && (
                  <SelectItem value={ownOrgId}>{t('production.defaultWeights.thisOrganization')}</SelectItem>
                )}
                {clientOptions.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </>
        ) : (
          // No clients: nothing to pick, but the target is still stated.
          <p className="text-sm">
            <span className="font-medium">{t('production.defaultWeights.appliesTo')}</span>{' '}
            <span className="text-muted-foreground">{t('production.defaultWeights.thisOrganization')}</span>
          </p>
        )}
        <p id="default-weights-client-help" className="text-xs text-muted-foreground">
          {t('production.defaultWeights.appliesToHelp')}
        </p>
        {formDirty && (
          <p id="default-weights-client-locked" className="text-xs text-muted-foreground">
            {t('production.defaultWeights.switchLocked')}
          </p>
        )}
      </div>

      <DefaultWeightsForm
        key={selectedOrgId}
        organizationId={targetOrgId}
        clientName={clientName}
        admin={admin}
        onDirtyChange={setFormDirty}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Form: code -> kg rows for ONE org.
// ---------------------------------------------------------------------------

interface DefaultWeightsFormProps {
  /** Target org; `undefined` = the caller's own org. */
  organizationId: string | undefined;
  /** Display name when editing a client, for the save bar summary. */
  clientName: string | undefined;
  admin: boolean;
  onDirtyChange: (dirty: boolean) => void;
}

function DefaultWeightsForm({
  organizationId,
  clientName,
  admin,
  onDirtyChange,
}: DefaultWeightsFormProps): JSX.Element {
  const { t } = useTranslation('settings');
  const idBase = useId();
  const utils = trpc.useUtils();
  const weightsQuery = trpc.defaultWeights.getWeights.useQuery({ organizationId });
  // The SELECTED org's own product-sync vocabulary. The form never gates on
  // this query: the pick-list degrades to manual entry instead.
  const codeTablesQuery = trpc.productSync.getCodeTablesForOrg.useQuery({ organizationId });
  const updateMutation = trpc.defaultWeights.updateWeights.useMutation();

  const baseline = weightsQuery.data?.weights;
  const baselineDraft = useMemo(() => toDraft(baseline ?? {}), [baseline]);
  // null = clean: render the server value, so a background refetch can update
  // the table, but never overwrite edits in progress.
  const [draft, setDraft] = useState<Record<string, string> | null>(null);
  const rows = draft ?? baselineDraft;

  const codes = useMemo(() => Object.keys(rows).sort((a, b) => a.localeCompare(b)), [rows]);
  const parsedByCode = useMemo(
    () => new Map(codes.map((code) => [code, parseKg(rows[code] ?? '')])),
    [codes, rows],
  );
  const anyInvalid = [...parsedByCode.values()].some((p) => p.kind !== 'ok');
  const dirty =
    admin && draft !== null && baseline !== undefined && !matchesBaseline(draft, baseline);

  useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);
  // An unmounted form holds no edits, so it must not leave the selector locked.
  useEffect(() => () => onDirtyChange(false), [onDirtyChange]);

  // ---- focus the kg input of a row the admin just added ----

  const kgInputs = useRef(new Map<string, HTMLInputElement>());
  const [focusCode, setFocusCode] = useState<string | null>(null);
  // Set when a pick came from the popover: Radix would otherwise hand focus
  // back to the trigger after it closes, undoing the move to the new row.
  const focusAfterPickRef = useRef(false);
  useEffect(() => {
    if (!focusCode) return;
    const input = kgInputs.current.get(focusCode);
    if (!input) return;
    input.focus();
    setFocusCode(null);
  }, [focusCode, rows]);

  // ---- local-edit mutators ----

  const edit = (update: (current: Record<string, string>) => Record<string, string>): void =>
    setDraft((prev) => update(prev ?? baselineDraft));

  // Codes whose kg field lost focus (or took Enter) since its last edit. With the shared
  // `kgFieldError` rule, a half-typed prefix ("0", "0.") only shows as an error
  // once committed, while text no continuation can fix ("45") shows at once.
  const [committedCodes, setCommittedCodes] = useState<ReadonlySet<string>>(() => new Set());
  const markCommitted = (code: string, committed: boolean): void =>
    setCommittedCodes((cur) => {
      if (cur.has(code) === committed) return cur;
      const next = new Set(cur);
      if (committed) next.add(code);
      else next.delete(code);
      return next;
    });

  const setKg = (code: string, text: string): void => {
    edit((cur) => ({ ...cur, [code]: text }));
    markCommitted(code, false);
  };

  const removeCode = (code: string): void =>
    edit((cur) => {
      const next = { ...cur };
      delete next[code];
      return next;
    });

  const addCode = (raw: string): boolean => {
    const code = raw.trim().toUpperCase();
    if (!RIP_CODE_REGEX.test(code)) {
      toast.error(t('production.codeOptions.invalidCode'));
      return false;
    }
    if (Object.prototype.hasOwnProperty.call(rows, code)) {
      toast.error(t('production.defaultWeights.alreadyInList', { code }));
      return false;
    }
    if (codes.length >= DEFAULT_WEIGHTS_MAX_CODES) {
      toast.error(t('production.defaultWeights.tooMany', { max: DEFAULT_WEIGHTS_MAX_CODES }));
      return false;
    }
    edit((cur) => ({ ...cur, [code]: '' }));
    focusAfterPickRef.current = pickerStatus === 'loaded';
    setFocusCode(code);
    return true;
  };

  // ---- client code-table pick-list ----

  const { typeOptions, typeStoreNamesByCode } = useMemo(
    () =>
      buildProductTypeCodeOptions(codeTablesQuery.data?.productTypes, codes, {
        custom: t('production.printSizeRules.badgeCustom'),
        singleSize: t('production.printSizeRules.badgeSingleSize'),
        alreadyAdded: t('production.printSizeRules.alreadyAdded'),
        invalidCode: t('production.codeOptions.invalidCode'),
      }),
    [codeTablesQuery.data, codes, t],
  );
  const discoveryStatus = codeTablesStatus(codeTablesQuery, typeOptions.length);
  // Keep manual entry usable while the code table loads, is empty (Threadheads
  // has none) or failed: the primitive disables its input in 'loading', so
  // anything short of a real pick-list renders as the plain manual field.
  const pickerStatus = discoveryStatus === 'loaded' ? 'loaded' : 'error';
  const pickerHint =
    discoveryStatus === 'error'
      ? t('production.defaultWeights.manualCodeHint')
      : discoveryStatus === 'empty'
        ? t('production.defaultWeights.noCodeTable')
        : undefined;

  // The org's codes with no row yet (and not covered by a plain code's row),
  // capped so one click can never push the table past the backend limit.
  const remainingCodes = useMemo(() => {
    if (discoveryStatus !== 'loaded') return [];
    const candidates = typeOptions.filter((o) => !o.disabled).map((o) => o.code);
    const present = new Set([...codes, ...candidates]);
    return candidates
      .filter((code) => !coveredByPlainCode(code, present))
      .slice(0, Math.max(0, DEFAULT_WEIGHTS_MAX_CODES - codes.length));
  }, [discoveryStatus, typeOptions, codes]);

  const addRemaining = (): void => {
    const added = remainingCodes.filter((code) => !Object.prototype.hasOwnProperty.call(rows, code));
    edit((cur) => {
      const next = { ...cur };
      for (const code of added) if (!(code in next)) next[code] = '';
      return next;
    });
    // Rows render sorted, so the first one to fill is the first in that order.
    const first = [...added].sort((a, b) => a.localeCompare(b))[0];
    if (first) setFocusCode(first);
  };

  // ---- save ----

  const save = async (): Promise<void> => {
    if (anyInvalid) {
      const message = t('production.defaultWeights.fixToSave');
      toast.error(message);
      throw new Error(message);
    }
    const weights: Record<string, number> = {};
    for (const [code, parsed] of parsedByCode) if (parsed.kind === 'ok') weights[code] = parsed.kg;
    try {
      const result = await updateMutation.mutateAsync({ organizationId, weights });
      // Show exactly what the server stored before the refetch lands.
      utils.defaultWeights.getWeights.setData({ organizationId }, result);
    } catch (error) {
      toast.error(
        t('production.defaultWeights.saveError', {
          message: error instanceof Error ? error.message : String(error),
        }),
      );
      throw error;
    }
    setDraft(null);
    void utils.defaultWeights.getWeights.invalidate({ organizationId });
  };

  const discard = (): void => setDraft(null);

  useDirtyRegistration(
    'default-weights',
    dirty
      ? {
          label: clientName
            ? t('production.defaultWeights.dirtyLabelClient', { client: clientName })
            : t('production.defaultWeights.title'),
          save,
          discard,
        }
      : null,
  );

  // ---- states ----

  // A failed background refetch keeps the loaded table (and any draft) on
  // screen; only a first load with nothing to show becomes the failure row.
  if (!baseline && weightsQuery.isError) {
    return (
      <div className="flex max-w-2xl flex-wrap items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
        <AlertTriangle className="h-5 w-5 shrink-0 text-amber-600" aria-hidden="true" />
        <p className="min-w-0 flex-1 text-sm text-amber-800">{t('production.defaultWeights.loadError')}</p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-10 gap-2 border-amber-300 bg-white/70 text-amber-900 hover:bg-white"
          onClick={() => void weightsQuery.refetch()}
        >
          <RefreshCw className="h-4 w-4" aria-hidden="true" />
          {t('production.common.retry')}
        </Button>
      </div>
    );
  }

  if (!baseline) {
    return (
      <div className="max-w-2xl space-y-2" role="status" aria-busy="true">
        <span className="sr-only">{t('production.defaultWeights.loading')}</span>
        <Skeleton className="h-11 w-full" />
        <Skeleton className="h-11 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {codes.length === 0 ? (
        <div className="flex max-w-2xl flex-col items-center gap-3 rounded-lg border border-dashed py-8 text-center">
          <div className="rounded-full bg-muted p-3">
            <Scale className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
          </div>
          <div className="space-y-1">
            <p className="text-sm font-medium">{t('production.defaultWeights.emptyTitle')}</p>
            <p className="mx-auto max-w-sm text-sm text-muted-foreground">
              {admin ? t('production.defaultWeights.emptyAdmin') : t('production.defaultWeights.emptyReadOnly')}
            </p>
          </div>
        </div>
      ) : (
        <div className="max-w-2xl overflow-hidden rounded-lg border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50 text-left">
                <th scope="col" className="w-2/5 px-3 py-2 font-medium">
                  {t('production.defaultWeights.colProductType')}
                </th>
                <th scope="col" className="px-3 py-2 font-medium">
                  {t('production.defaultWeights.colWeight')}
                </th>
                {admin && (
                  <th scope="col" className="w-14 px-2 py-2">
                    <span className="sr-only">{t('production.defaultWeights.colRemove')}</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {codes.map((code, index) => {
                const text = rows[code] ?? '';
                const parsed = parsedByCode.get(code) ?? { kind: 'blank' as const };
                const storeNames = typeStoreNamesByCode.get(code);
                const showError = kgFieldError(text, committedCodes.has(code)) !== null;
                const changed =
                  !Object.prototype.hasOwnProperty.call(baseline, code) ||
                  parsed.kind !== 'ok' ||
                  parsed.kg !== baseline[code];
                const metaId = `${idBase}-kg-${index}-meta`;
                const meta =
                  parsed.kind === 'ok'
                    ? t('production.defaultWeights.grams', {
                        grams: Math.round(parsed.kg * 1000).toLocaleString(),
                      })
                    : parsed.kind === 'blank'
                      ? t('production.defaultWeights.enterWeight')
                      : parsed.kind === 'tooPrecise'
                        ? t('production.defaultWeights.tooPrecise')
                        : t('production.defaultWeights.validation', {
                            min: DEFAULT_WEIGHT_MIN_KG,
                            max: DEFAULT_WEIGHT_MAX_KG,
                          });
                return (
                  <tr key={code} className="border-b last:border-b-0">
                    <th scope="row" className="px-3 py-2 text-left align-middle font-normal">
                      <span className="font-mono font-semibold">{code}</span>
                      {storeNames && storeNames.length > 0 && (
                        <span className="mt-0.5 block text-xs text-muted-foreground">
                          {joinStoreNames(storeNames)}
                        </span>
                      )}
                    </th>
                    <td className="px-3 py-2 align-middle">
                      {admin ? (
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                          <Input
                            type="text"
                            inputMode="decimal"
                            autoComplete="off"
                            spellCheck={false}
                            placeholder="0.2"
                            value={text}
                            aria-label={t('production.defaultWeights.weightForCode', { code })}
                            aria-invalid={showError || undefined}
                            aria-describedby={metaId}
                            className={cn(
                              'h-11 w-28 font-mono tabular-nums',
                              changed && 'border-threadheads-purple',
                              showError && 'border-destructive',
                            )}
                            onChange={(e) => setKg(code, e.target.value)}
                            onBlur={() => markCommitted(code, true)}
                            // Enter commits like blur (the shared kg rule),
                            // except an IME's Enter confirming a candidate
                            // (Safari sends that one with isComposing false
                            // and keyCode 229).
                            onKeyDown={(e) => {
                              if (e.nativeEvent.isComposing || e.keyCode === 229) return;
                              if (e.key === 'Enter') markCommitted(code, true);
                            }}
                            ref={(el) => {
                              if (el) kgInputs.current.set(code, el);
                              else kgInputs.current.delete(code);
                            }}
                          />
                          <span
                            id={metaId}
                            className={cn(
                              'text-xs tabular-nums',
                              showError ? 'text-destructive' : 'text-muted-foreground',
                            )}
                          >
                            {meta}
                          </span>
                        </div>
                      ) : (
                        <span className="flex items-baseline gap-3">
                          <span className="font-mono tabular-nums">{text}</span>
                          <span className="text-xs tabular-nums text-muted-foreground">{meta}</span>
                        </span>
                      )}
                    </td>
                    {admin && (
                      <td className="px-2 py-2 text-right align-middle">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-11 w-11 text-muted-foreground hover:text-foreground"
                          aria-label={t('production.defaultWeights.removeCode', { code })}
                          onClick={() => removeCode(code)}
                        >
                          <Trash2 className="h-4 w-4" aria-hidden="true" />
                        </Button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {admin && (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
          <PrintSizeCodeCombobox
            options={typeOptions}
            status={pickerStatus}
            onSelect={addCode}
            placeholder={t('production.printSizeRules.typeComboboxPlaceholder')}
            ariaLabel={t('production.printSizeRules.typeComboboxAriaLabel')}
            addButtonLabel={t('production.printSizeRules.addProductType')}
            createLabel={(code) => t('production.printSizeRules.createCode', { code })}
            emptyLabel={t('production.codeOptions.noMatches')}
            errorHint={pickerHint}
            onCloseAutoFocus={(event) => {
              if (!focusAfterPickRef.current) return;
              event.preventDefault();
              focusAfterPickRef.current = false;
            }}
          />
          {remainingCodes.length > 0 && (
            <Button type="button" variant="outline" className="h-11 gap-1" onClick={addRemaining}>
              <Plus className="h-4 w-4" aria-hidden="true" />
              {t('production.defaultWeights.addRemaining', { count: remainingCodes.length })}
            </Button>
          )}
        </div>
      )}

      <p className="max-w-prose text-xs text-muted-foreground">{t('production.defaultWeights.dualPrintHint')}</p>

      {/* Saving is the sticky save bar's job; this only says why it will refuse. */}
      {admin && dirty && anyInvalid && (
        <p className="max-w-2xl text-xs text-amber-700" role="status">
          {t('production.defaultWeights.fixToSave')}
        </p>
      )}

      {!admin && (
        <p className="text-sm text-muted-foreground">{t('production.defaultWeights.adminOnly')}</p>
      )}
    </div>
  );
}
